import {Inject, Injectable} from '@angular/core';
import {NAVIGATION_PREFERENCES_STORAGE} from './navigation-group-preferences.service';
import {Preferences} from '@capacitor/preferences';
import {Subject} from 'rxjs';
import {QuickNavigationPosition, readQuickNavigationPosition} from './quick-navigation-position';

@Injectable({providedIn: 'root'})
export class QuickNavigationPreferencesService {
  readonly transparencyChanges = new Subject<{accountId: string; value: number}>();
  constructor(@Inject(NAVIGATION_PREFERENCES_STORAGE) private readonly storage: Pick<typeof Preferences, 'get' | 'set'>) {}

  async read(accountId: string): Promise<QuickNavigationPosition | null> {
    if (!accountId) return null;
    try {
      const {value} = await this.storage.get({key: this.key(accountId)});
      return value ? readQuickNavigationPosition(JSON.parse(value)) : null;
    } catch {
      return null;
    }
  }

  async write(accountId: string, position: QuickNavigationPosition): Promise<void> {
    if (!accountId) return;
    try {
      await this.storage.set({key: this.key(accountId), value: JSON.stringify(position)});
    } catch {
      // The handle remains usable when device storage is unavailable.
    }
  }

  private key(accountId: string): string {
    return `invemory.quick-navigation.v1.${encodeURIComponent(accountId)}`;
  }

  async readTransparency(accountId: string): Promise<number> {
    if (!accountId) return 90;
    try {
      const {value} = await this.storage.get({key: `${this.key(accountId)}.transparency`});
      const parsed: unknown = value === null ? null : JSON.parse(value);
      return typeof parsed === 'number' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : 90;
    } catch {
      return 90;
    }
  }

  async writeTransparency(accountId: string, value: number): Promise<boolean> {
    if (!accountId || !Number.isFinite(value) || value < 0 || value > 100) return false;
    try {
      await this.storage.set({key: `${this.key(accountId)}.transparency`, value: JSON.stringify(value)});
      this.transparencyChanges.next({accountId, value});
      return true;
    } catch {
      return false;
    }
  }
}
